import { useEffect, useState } from "react";
import { Skeleton } from "~/components/ui/skeleton";
import { useProjectNeedsSetup } from "~/pages/manifold-nextgen/hooks/useProjectNeedsSetup";
import { NewSessionPicker } from "./NewSessionPicker";
import { ProjectSetupWizard } from "./ProjectSetupWizard";
import { useProjectContext } from "./useProjectContext";

type Mode = "loading" | "setup" | "session";

const LoadingSkeleton = () => (
  <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto p-8">
    <div className="flex w-full max-w-lg flex-col gap-6">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-72" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
      <div className="flex flex-col gap-1">
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-10 rounded-md" />
        ))}
      </div>
    </div>
  </div>
);

export const ProjectIndex = () => {
  const { attachedAgents, attachedDatasets, isOwner } = useProjectContext();
  const [mode, setMode] = useState<Mode>("loading");

  const isLoading = attachedAgents === undefined || attachedDatasets === undefined;
  const needsSetup = useProjectNeedsSetup(attachedDatasets, attachedAgents, isOwner);

  // "setup -> session" only happens via the wizard's explicit Start Session
  // button (below) — never automatically, so filling in the last requirement
  // mid-setup doesn't yank the owner into a session before they choose to.
  // "session -> setup" is the opposite: if the project stops being usable
  // (e.g. the last enabled agent is removed from the context sidebar while
  // sitting on this page), fall back to setup immediately, no extra click.
  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (mode === "loading") {
      setMode(needsSetup ? "setup" : "session");
    } else if (mode === "session" && needsSetup) {
      setMode("setup");
    }
  }, [mode, isLoading, needsSetup]);

  if (mode === "loading") {
    return <LoadingSkeleton />;
  }

  if (mode === "setup") {
    return <ProjectSetupWizard onContinue={() => setMode("session")} />;
  }

  return <NewSessionPicker />;
};
